import { useWelcomeOffer, WelcomePrice } from '../offers/WelcomeOfferContext';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { subscriptionService } from '../api/subscriptionService';
import { useI18n, rich } from '../i18n';
import { detectCountry, getDisplayCurrency, getAllPlanPrices, getCachedCountry } from '../utils/pricing';

const AlertCircleIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const UnlockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="flex-shrink-0">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PAID_PLANS = ['starter', 'pro', 'max'];

const SuspendedModal = () => {
  const { t } = useI18n();
  const [showPlans, setShowPlans] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [pricingCurrency, setPricingCurrency] = useState(() =>
    getDisplayCurrency({ geoCountry: getCachedCountry() })
  );
  const { offer: welcomeOffer } = useWelcomeOffer();
  const planPrices = getAllPlanPrices(pricingCurrency);

  const cachedPlan = localStorage.getItem('arabcap_user_plan') || 'free';
  const isAlreadyPaid = PAID_PLANS.includes(cachedPlan);

  useEffect(() => {
    let cancelled = false;
    detectCountry().then((geoCountry) => {
      if (!cancelled) {
        setPricingCurrency(getDisplayCurrency({ geoCountry }));
      }
    });
    return () => { cancelled = true; };
  }, []);

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/212691154252?text=Hi,%20my%20account%20has%20been%20suspended.%20I%20would%20like%20to%20understand%20why%20and%20resolve%20this%20issue.', '_blank', 'noopener,noreferrer');
  };

  const handleUpgradeToPlan = async (plan) => {
    try {
      setUpgradeLoading(true);
      setBillingError(null);
      const result = await subscriptionService.createCheckoutSession(plan, pricingCurrency, welcomeOffer?.plans.includes(plan) ? welcomeOffer.percent_off : null);
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      setBillingError(t('Failed to start checkout. Please try again.'));
      setUpgradeLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/90 backdrop-blur-xl"
      style={{ animation: 'fadeIn 0.3s ease-out' }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      
      {(!showPlans || isAlreadyPaid) ? (
        <div 
          className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
          style={{ animation: 'slideUp 0.4s ease-out' }}
        >
          {/* Header with accent color */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6">
            <div className="flex flex-col items-center text-white">
              <div className="mb-3">
                <AlertCircleIcon />
              </div>
              <h2 className="text-2xl font-bold text-center">
                {t('Account Suspended')}
              </h2>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            <div className="mb-6">
              <p className="text-slate-700 text-center text-lg leading-relaxed">
                {t('Your account has been suspended due to unusual activity detected on your account.')}
              </p>
            </div>

            <div className="mb-6 bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-sm text-slate-600 text-center">
                {t(isAlreadyPaid
                  ? 'If you believe this is a mistake or would like to discuss this matter, please contact our support team.'
                  : 'Upgrade to a paid plan to instantly unblock your account, or contact our support team.')}
              </p>
            </div>

            {/* Upgrade to Unblock Button — only for non-paid users */}
            {!isAlreadyPaid && (
              <button
                onClick={() => { setShowPlans(true); setBillingError(null); }}
                className="w-full bg-gradient-to-r from-[#f73140] to-[#f73140]/90 hover:from-[#e02030] hover:to-[#e02030]/90 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-3 mb-3"
              >
                <UnlockIcon />
                <span>{t('Upgrade to Unblock')}</span>
              </button>
            )}

            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsAppClick}
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
            >
              <WhatsAppIcon />
              <span>{t('Contact Us on WhatsApp')}</span>
            </button>

            <div className="mt-4 text-center">
              <p className="text-xs text-slate-500">
                {t('We typically respond within a few hours')}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div 
          className="relative w-full max-w-4xl mx-4 bg-[#f5f5f5] rounded-2xl sm:rounded-3xl shadow-2xl max-h-[95vh] overflow-y-auto"
          style={{ animation: 'slideUp 0.4s ease-out' }}
        >
          {/* Back button */}
          <button
            onClick={() => setShowPlans(false)}
            className="absolute top-3 start-3 z-10 w-8 h-8 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 flex items-center justify-center transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {/* Modal Header */}
          <div className="text-center pt-6 sm:pt-8 pb-3 sm:pb-4 px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900">{t('Upgrade to Unblock')}</h2>
            <p className="text-sm text-slate-500 mt-2">{t('Choose a plan and your account will be automatically unblocked')}</p>
          </div>

          {/* Modal Content */}
          <div className="px-3 sm:px-6 pb-6 sm:pb-8">
            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
              {/* Starter Plan */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col">
                <div className="mb-3 sm:mb-4">
                  <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-1 sm:mb-2">STARTER</h3>
                  <p className="text-xs sm:text-sm text-slate-500">{t('Perfect for beginner video editors who are just getting started.')}</p>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t('8 videos')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t(planPrices.starter.perVideo)}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t('Email support')}</span>
                  </li>
                </ul>
                <div className="mt-auto">
                  <div className="text-center mb-3 sm:mb-4">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900">{t(planPrices.starter.display)}</span>
                    <span className="text-slate-500 text-xs sm:text-sm">{t('/month')}</span>
                  </div>
                  <button
                    onClick={() => handleUpgradeToPlan('starter')}
                    disabled={upgradeLoading}
                    className="w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {t(upgradeLoading ? 'Processing...' : 'GET STARTED')}
                  </button>
                </div>
              </div>

              {/* Pro Plan - Featured */}
              <div className="bg-[#f73140] rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg flex flex-col relative order-first md:order-none">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-slate-900 text-white text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wide">{t('Popular')}</span>
                </div>
                <div className="mb-3 sm:mb-4 mt-2 sm:mt-0">
                  <h3 className="text-lg sm:text-xl font-black text-white mb-1 sm:mb-2">PRO</h3>
                  <p className="text-xs sm:text-sm text-white/80">{t('Great for active editors working with more clients and content.')}</p>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                  <li className="flex items-center gap-2 text-white">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className="text-xs sm:text-sm">{t('30 videos')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-white">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-white">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className="text-xs sm:text-sm">{t(planPrices.pro.perVideo)}</span>
                  </li>
                  <li className="flex items-center gap-2 text-white">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className="text-xs sm:text-sm">{t('Priority support')}</span>
                  </li>
                </ul>
                <div className="mt-auto">
                  <div className="text-center mb-3 sm:mb-4">
                    <span className="text-3xl sm:text-4xl font-black text-white">{<WelcomePrice plan="pro" price={planPrices.pro} />}</span>
                    <span className="text-white/80 text-xs sm:text-sm">{t('/month')}</span>
                  </div>
                  <button
                    onClick={() => handleUpgradeToPlan('pro')}
                    disabled={upgradeLoading}
                    className="w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all bg-white text-[#f73140] hover:bg-slate-50 disabled:opacity-50"
                  >
                    {t(upgradeLoading ? 'Processing...' : 'GO PRO')}
                  </button>
                </div>
              </div>

              {/* Max Plan */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col">
                <div className="mb-3 sm:mb-4">
                  <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-1 sm:mb-2">MAX</h3>
                  <p className="text-xs sm:text-sm text-slate-500">{t('Built for professional editors handling large content demands.')}</p>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t('100 videos')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t(planPrices.max.perVideo)}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <span className="text-[#f73140]"><CheckIcon /></span>
                    <span className="text-xs sm:text-sm">{t('Dedicated support')}</span>
                  </li>
                </ul>
                <div className="mt-auto">
                  <div className="text-center mb-3 sm:mb-4">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900">{<WelcomePrice plan="max" price={planPrices.max} />}</span>
                    <span className="text-slate-500 text-xs sm:text-sm">{t('/month')}</span>
                  </div>
                  <button
                    onClick={() => handleUpgradeToPlan('max')}
                    disabled={upgradeLoading}
                    className="w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {t(upgradeLoading ? 'Processing...' : 'GET PREMIUM')}
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {billingError && (
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl bg-red-50 border border-red-100">
                <p className="text-xs sm:text-sm text-red-600 text-center">{billingError}</p>
              </div>
            )}

            {/* Benefits Footer */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mt-5 sm:mt-8">
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="w-4 h-4 sm:w-5 sm:h-5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{t('Instant unblock')}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="w-4 h-4 sm:w-5 sm:h-5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{t('Cancel anytime')}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="w-4 h-4 sm:w-5 sm:h-5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{t('Customer support')}</span>
              </div>
            </div>

            {/* Contact Info */}
            <div className="text-center mt-4 sm:mt-6">
              <p className="text-xs sm:text-sm text-slate-600">
                {rich(t('Contact us on WhatsApp at <a>+212 691 154 252</a> for a custom offer.'), {
                  a: (c) => <a href="https://wa.me/212691154252" target="_blank" rel="noopener noreferrer" dir="ltr" className="font-bold text-slate-900 hover:text-[#f73140] transition-colors">{c}</a>,
                })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default SuspendedModal;
